import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/client';
import { clearRejectedSession, rememberSession } from '../../platform/host';
import * as authApi from './api';
import type { LoginResponse, UserProfile } from './api';

/** A login that has passed the password and is waiting for a second factor. */
export interface PendingMfa {
  token: string;
}

interface AuthState {
  user: UserProfile | null;
  loading: boolean;
  /** Set when the password was right but a code is still needed. */
  pendingMfa: PendingMfa | null;
  signIn: (email: string, password: string) => Promise<void>;
  submitMfa: (code: string, kind: 'totp' | 'recovery') => Promise<void>;
  cancelMfa: () => void;
  signInWithPasskey: (email: string | null) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingMfa, setPendingMfa] = useState<PendingMfa | null>(null);

  // The session lives in an HttpOnly cookie the page cannot read, so the only
  // way to know whether one is active is to ask.
  useEffect(() => {
    authApi
      .fetchMe()
      .then(setUser)
      .catch((error: unknown) => {
        if (!(error instanceof ApiError) || error.status !== 401) {
          console.error('Failed to restore session', error);
        }
        // A token the server actually rejected is spent, and keeping it would
        // send a dead credential with every request from now on. One that was
        // never sent — held back by the phone's lock — is left alone.
        void clearRejectedSession();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const accept = useCallback(async (response: LoginResponse) => {
    if (response.mfa_required && response.mfa_token) {
      // Deliberately not a session yet: everything downstream keeps treating
      // the user as signed out until the second factor lands.
      setPendingMfa({ token: response.mfa_token });
      return;
    }
    // Nothing in a browser: there the session is in a cookie and this is null.
    await rememberSession(response.session_token);
    setPendingMfa(null);
    setUser(response.user);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      await accept(await authApi.login(email, password));
    },
    [accept],
  );

  const submitMfa = useCallback(
    async (code: string, kind: 'totp' | 'recovery') => {
      if (!pendingMfa) throw new ApiError(400, 'mfa_challenge_invalid');
      const finish = kind === 'totp' ? authApi.loginTotp : authApi.loginRecovery;
      await accept(await finish(pendingMfa.token, code));
    },
    [pendingMfa, accept],
  );

  // A challenge is single use, so going back has to mean starting over rather
  // than holding a token the server has already spent.
  const cancelMfa = useCallback(() => setPendingMfa(null), []);

  const signInWithPasskey = useCallback(
    async (email: string | null) => {
      await accept(await authApi.signInWithPasskey(email));
    },
    [accept],
  );

  const signOut = useCallback(async () => {
    await authApi.logout();
    // Order matters: the request above is the one that needs the token.
    await rememberSession(null);
    setUser(null);
    setPendingMfa(null);
  }, []);

  /** Re-read the profile after something that changed it — enrolling a factor. */
  const refresh = useCallback(async () => {
    setUser(await authApi.fetchMe());
  }, []);

  const can = useCallback(
    (permission: string) => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      pendingMfa,
      signIn,
      submitMfa,
      cancelMfa,
      signInWithPasskey,
      signOut,
      refresh,
      can,
    }),
    [
      user,
      loading,
      pendingMfa,
      signIn,
      submitMfa,
      cancelMfa,
      signInWithPasskey,
      signOut,
      refresh,
      can,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
